/**
 * chordUtils — Chord parsing + transposition for the Worship module.
 *
 * Supports two conventions, auto-detected:
 *   1. ChordPro inline:  `[G]Amazing [D/F#]grace how sweet the [C]sound`
 *   2. Chord-line-above-lyric-line pairs found in the song's `chords` field:
 *          G      D/F#      C
 *          Amazing grace how sweet the sound
 *
 * `transposeChord(chord, semitones)` handles slash chords like D/F# and
 * suffixes like m7, maj7, sus4, add9 etc. — the root + optional bass are
 * transposed, the suffix passed through untouched.
 */

const SHARP = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT  = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

const NOTE_RE = /^([A-G])(#|b|##|bb)?/;
const CHORD_RE = /^([A-G](?:#|b)?)(m|maj|min|dim|aug|sus|add)?([^\/\s]*)(\/([A-G](?:#|b)?))?$/i;

// Prefer flats when the target key has flats
const FLAT_KEYS = new Set(['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb', 'Dm', 'Gm', 'Cm', 'Fm', 'Bbm']);

function noteIndex(note) {
  if (!note) return -1;
  const n = note.replace('b', 'b').replace('#', '#');
  const sharpIdx = SHARP.indexOf(n);
  if (sharpIdx >= 0) return sharpIdx;
  const flatIdx = FLAT.indexOf(n);
  return flatIdx;
}

function noteFromIndex(idx, useFlats) {
  const i = ((idx % 12) + 12) % 12;
  return useFlats ? FLAT[i] : SHARP[i];
}

/** Transpose a single chord token like "D/F#" or "Cmaj7" by N semitones. */
export function transposeChord(chord, semitones, targetKey) {
  if (!chord || !semitones) return chord || '';
  const trimmed = chord.trim();
  const m = trimmed.match(CHORD_RE);
  if (!m) return chord;
  const [, root, quality = '', extension = '', , bass] = m;
  const useFlats = targetKey ? FLAT_KEYS.has(targetKey) : (semitones < 0);
  const rootIdx = noteIndex(root);
  if (rootIdx < 0) return chord;
  const newRoot = noteFromIndex(rootIdx + semitones, useFlats);
  let out = newRoot + (quality || '') + (extension || '');
  if (bass) {
    const bassIdx = noteIndex(bass);
    if (bassIdx >= 0) out += '/' + noteFromIndex(bassIdx + semitones, useFlats);
    else out += '/' + bass;
  }
  return out;
}

/** Compute the semitone distance between two keys. */
export function semitonesBetween(fromKey, toKey) {
  const a = noteIndex((fromKey || '').match(NOTE_RE)?.[0]);
  const b = noteIndex((toKey || '').match(NOTE_RE)?.[0]);
  if (a < 0 || b < 0) return 0;
  return ((b - a) % 12 + 12) % 12;
}

/** Parse a ChordPro line into segments of `{ chord?, text }`. */
export function parseChordProLine(line) {
  if (!line) return [{ text: '' }];
  const segments = [];
  const re = /\[([^\]]+)\]/g;
  let last = 0;
  let m;
  let pendingChord = null;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) {
      segments.push({ chord: pendingChord, text: line.slice(last, m.index) });
      pendingChord = null;
    } else if (pendingChord) {
      // two chords in a row before text — merge with a space
      pendingChord += ' ' + m[1];
    }
    pendingChord = pendingChord ? pendingChord + ' ' + m[1] : m[1];
    last = m.index + m[0].length;
  }
  if (last < line.length || pendingChord) {
    segments.push({ chord: pendingChord, text: line.slice(last) });
  }
  return segments.length ? segments : [{ text: line }];
}

/** Auto-detect ChordPro inline notation anywhere in the lyrics blob. */
export function hasChordPro(text) {
  if (!text) return false;
  // needs at least one bracketed token that looks like a chord
  return /\[[A-G][#b]?[^\]]{0,8}\]/.test(text);
}

/** Detect if a `chords` field contains chord-over-lyric line pairs. */
export function hasChordChart(chordsText) {
  if (!chordsText || !chordsText.trim()) return false;
  // Any line with only chord-looking tokens?
  const lines = chordsText.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const tokens = line.split(/\s+/);
    if (tokens.length === 0) continue;
    const chordLike = tokens.every((t) => /^[A-G](#|b)?(m|maj|min|dim|aug|sus|add)?[0-9]*(\/[A-G](#|b)?)?$/.test(t));
    if (chordLike && tokens.length >= 2) return true;
  }
  return false;
}

/** Pair up chord/lyric lines from a `chords` field: odd lines are chords, even are lyrics. */
export function pairChordChart(chordsText) {
  if (!chordsText) return [];
  const lines = chordsText.split('\n');
  const pairs = [];
  for (let i = 0; i < lines.length; i += 2) {
    pairs.push({ chords: lines[i] || '', lyric: lines[i + 1] || '' });
  }
  return pairs;
}
